import { io, Socket } from 'socket.io-client';
import { API_BASE_URL, ChatMessage } from './api';

type ServerToClientEvents = {
  'chat:connected': (payload: { userId: number; organizationId: number }) => void;
  'chat:conversation:updated': (payload: { conversationId: number }) => void;
  'chat:unread:changed': (payload: { conversationId: number }) => void;
  'chat:message:new': (payload: { conversationId: number; message: ChatMessage }) => void;
  'chat:message:read': (payload: {
    conversationId: number;
    userId: number;
    lastReadMessageId?: number | null;
    lastReadAt?: string | null;
    unreadCount: number;
  }) => void;
  'chat:typing': (payload: { conversationId: number; userId: number; isTyping: boolean }) => void;
  'chat:user:online': (payload: { userId: number }) => void;
  'chat:user:offline': (payload: { userId: number }) => void;
  'chat:presence:snapshot': (payload: { userIds: number[] }) => void;
};

type ClientToServerEvents = {
  'chat:join': (payload: { conversationId: number }) => void;
  'chat:leave': (payload: { conversationId: number }) => void;
  'chat:typing:start': (payload: { conversationId: number }) => void;
  'chat:typing:stop': (payload: { conversationId: number }) => void;
};

let chatSocket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
let activeToken: string | null = null;

export const ensureChatSocketConnection = (token: string) => {
  if (!token) {
    return null;
  }

  if (chatSocket && activeToken === token) {
    if (!chatSocket.connected) {
      chatSocket.connect();
    }
    return chatSocket;
  }

  if (chatSocket) {
    chatSocket.disconnect();
  }

  activeToken = token;
  chatSocket = io(`${API_BASE_URL.replace(/\/+$/, '')}/chat`, {
    transports: ['websocket'],
    autoConnect: true,
    withCredentials: true,
    auth: {
      token: `Bearer ${token}`,
    },
  });

  return chatSocket;
};

export const getChatSocket = () => chatSocket;

export const disconnectChatSocket = () => {
  chatSocket?.disconnect();
  chatSocket = null;
  activeToken = null;
};

export const emitChatEvent = <TEvent extends keyof ClientToServerEvents>(
  event: TEvent,
  ...args: Parameters<ClientToServerEvents[TEvent]>
) => {
  chatSocket?.emit(event, ...args);
};